import { readCachedUser } from "../utils/cachedUser";
import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import NotificationBell from "../components/NotificationBell";
import Icon from "../components/Icon";

const css = `
*{box-sizing:border-box;margin:0;padding:0;}
  .mc{min-height:100vh;background:var(--bg);color:var(--text);font-family:'Prompt',sans-serif;}
  .mc-nav{display:flex;align-items:center;justify-content:space-between;padding:18px 40px;background:var(--surface);border-bottom:1px solid var(--border);position:sticky;top:0;z-index:10;}
  .mc-logo-img{height:80px;width:auto;mix-blend-mode:screen;filter:brightness(1.1);cursor:pointer;}
  .mc-back{padding:7px 16px;border-radius:var(--radius-full);border:1px solid var(--border);background:transparent;color:var(--text-muted);font-family:'Prompt',sans-serif;font-size:0.8rem;cursor:pointer;transition:all 0.2s;}
  .mc-back:hover{border-color:var(--accent);color:var(--accent);}
  .mc-body{max-width:760px;margin:0 auto;padding:32px 24px 64px;}
  .mc-title{font-family:'DM Serif Display',serif;font-size:1.6rem;margin-bottom:4px;}
  .mc-subtitle{font-size:0.8rem;color:var(--text-muted);margin-bottom:24px;line-height:1.6;}
  .mc-stats{display:grid;grid-template-columns:repeat(3,1fr);gap:10px;margin-bottom:24px;}
  .mc-stat{background:var(--surface);border:1px solid var(--border);border-radius:12px;padding:14px 16px;}
  .mc-stat-label{font-size:0.68rem;letter-spacing:1.2px;text-transform:uppercase;color:var(--text-muted);margin-bottom:6px;}
  .mc-stat-val{font-family:'DM Serif Display',serif;font-size:1.45rem;}
  .mc-stat-val.accent{color:var(--accent);}
  .mc-sec{font-size:0.7rem;letter-spacing:1.5px;text-transform:uppercase;color:var(--text-muted);margin:24px 0 10px;}
  .mc-card{background:var(--surface);border:1px solid var(--border);border-radius:var(--radius);padding:16px;margin-bottom:10px;}
  .mc-card-top{display:flex;align-items:center;gap:12px;flex-wrap:wrap;}
  .mc-code{font-family:monospace;font-size:1.2rem;font-weight:700;letter-spacing:3px;color:var(--accent);background:var(--bg);border:1px dashed rgba(247,71,53,0.35);border-radius:10px;padding:8px 14px;}
  .mc-grow{flex:1;min-width:120px;}
  .mc-btn{padding:8px 14px;border-radius:10px;border:1px solid rgba(247,71,53,0.3);background:rgba(247,71,53,0.1);color:var(--accent);font-family:'Prompt',sans-serif;font-size:0.8rem;font-weight:600;cursor:pointer;display:inline-flex;align-items:center;gap:6px;}
  .mc-btn:hover{background:rgba(247,71,53,0.18);}
  .mc-meta{font-size:0.76rem;color:var(--text-muted);line-height:1.7;margin-top:10px;}
  .mc-meta b{color:var(--text);font-weight:600;}
  .mc-badge{display:inline-block;padding:2px 10px;border-radius:100px;font-size:0.68rem;font-weight:600;}
  .mc-badge.on{background:rgba(247,71,53,0.12);color:var(--accent);border:1px solid rgba(247,71,53,0.2);}
  .mc-badge.off{background:rgba(255,85,85,0.1);color:var(--danger);border:1px solid rgba(255,85,85,0.2);}
  .mc-item{display:flex;align-items:center;gap:12px;background:var(--surface);border:1px solid var(--border);border-radius:12px;padding:12px 14px;margin-bottom:8px;}
  .mc-item-body{flex:1;min-width:0;}
  .mc-item-name{font-size:0.88rem;font-weight:600;}
  .mc-item-meta{font-size:0.72rem;color:var(--text-muted);}
  .mc-item-amt{font-weight:700;color:var(--accent);white-space:nowrap;}
  .mc-empty{text-align:center;color:var(--text-muted);font-size:0.82rem;padding:28px 12px;background:var(--surface);border:1px dashed var(--border);border-radius:12px;line-height:1.7;}
  @media(max-width:600px){
    .mc-nav{padding:10px 16px;}
    .mc-logo-img{height:52px;}
    .mc-body{padding:20px 16px 48px;}
    .mc-stats{grid-template-columns:1fr 1fr;}
    .mc-code{font-size:1rem;letter-spacing:2px;}
  }
`;

const toMs = (ts) => (ts?.toMillis ? ts.toMillis() : ts ? new Date(ts).getTime() : 0);
const fmtDate = (ms) =>
  ms ? new Date(ms).toLocaleDateString("th-TH", { day: "numeric", month: "short", year: "2-digit" }) : "-";
const baht = (n) => `฿${(n || 0).toLocaleString()}`;

export default function MyCouponsPage() {
  const navigate = useNavigate();
  const user = readCachedUser();
  const [coupons, setCoupons] = useState(null);
  const [shareTxs, setShareTxs] = useState([]);
  const [copied, setCopied] = useState("");

  useEffect(() => {
    if (!user.uid) { navigate("/login"); return; }
    const unsubs = [
      onSnapshot(query(collection(db, "coupons"), where("ownerId", "==", user.uid)),
        (s) => setCoupons(s.docs.map((d) => ({ id: d.id, ...d.data() }))), () => setCoupons([])),
      onSnapshot(query(collection(db, "transactions"), where("couponOwnerId", "==", user.uid)),
        (s) => setShareTxs(s.docs.map((d) => ({ id: d.id, ...d.data() })).filter((t) => t.couponShare > 0)), () => {}),
    ];
    return () => unsubs.forEach((u) => u());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stats = useMemo(() => {
    const monthStart = new Date(); monthStart.setDate(1); monthStart.setHours(0, 0, 0, 0);
    const total = shareTxs.reduce((s, t) => s + t.couponShare, 0);
    const month = shareTxs.filter((t) => toMs(t.createdAt) >= monthStart.getTime()).reduce((s, t) => s + t.couponShare, 0);
    const byCode = {};
    shareTxs.forEach((t) => {
      const k = t.couponCode || "";
      if (!byCode[k]) byCode[k] = { deals: 0, amount: 0 };
      byCode[k].deals += 1;
      byCode[k].amount += t.couponShare;
    });
    const history = [...shareTxs].sort((a, b) => toMs(b.createdAt) - toMs(a.createdAt));
    return { total, month, byCode, history };
  }, [shareTxs]);

  const copy = (text, key) => {
    try { navigator.clipboard.writeText(text); } catch { /* ไม่รองรับ clipboard */ }
    setCopied(key);
    setTimeout(() => setCopied(""), 1800);
  };

  const totalUses = (coupons || []).reduce((s, c) => s + (c.usedCount || 0), 0);

  return (
    <div className="mc">
      <style>{css}</style>
      <nav className="mc-nav">
        <span className="kl-wordmark">klang.</span>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <NotificationBell />
          <button className="mc-back" onClick={() => navigate("/home")}>← กลับ</button>
        </div>
      </nav>

      <div className="mc-body">
        <div className="mc-title">คูปองของฉัน</div>
        <div className="mc-subtitle">
          แชร์รหัสคูปองให้คนอื่นใช้ลดค่าธรรมเนียม — เมื่อดีลที่ใช้คูปองของคุณสำเร็จ คุณได้ส่วนแบ่งจากค่าธรรมเนียมเข้ากระเป๋าอัตโนมัติ
        </div>

        {coupons === null ? (
          <div className="mc-empty">กำลังโหลด...</div>
        ) : coupons.length === 0 ? (
          <div className="mc-empty">คุณยังไม่มีคูปองของสมาชิก<br />ติดต่อแอดมินเพื่อขอรับคูปองสำหรับแชร์</div>
        ) : (
          <>
            <div className="mc-stats">
              <div className="mc-stat">
                <div className="mc-stat-label">ส่วนแบ่งรวม</div>
                <div className="mc-stat-val accent">{baht(stats.total)}</div>
              </div>
              <div className="mc-stat">
                <div className="mc-stat-label">เดือนนี้</div>
                <div className="mc-stat-val accent">{baht(stats.month)}</div>
              </div>
              <div className="mc-stat">
                <div className="mc-stat-label">ถูกใช้แล้ว</div>
                <div className="mc-stat-val">{totalUses} ครั้ง</div>
              </div>
            </div>

            <div className="mc-sec">รหัสคูปองของฉัน</div>
            {coupons.map((c) => {
              const exp = toMs(c.expiresAt);
              const expired = exp && exp < Date.now();
              const full = c.maxUses && c.usedCount >= c.maxUses;
              const active = c.isActive && !expired && !full;
              const s = stats.byCode[c.code] || { deals: 0, amount: 0 };
              return (
                <div key={c.id} className="mc-card">
                  <div className="mc-card-top">
                    <div className="mc-code">{c.code}</div>
                    <div className="mc-grow">
                      <span className={`mc-badge ${active ? "on" : "off"}`}>
                        {expired ? "หมดอายุ" : full ? "ใช้ครบแล้ว" : c.isActive ? "ใช้งานได้" : "ปิดใช้"}
                      </span>
                    </div>
                    <button className="mc-btn" onClick={() => copy(c.code, c.id)}>
                      <Icon name={copied === c.id ? "check" : "copy"} size={14} />
                      {copied === c.id ? "คัดลอกแล้ว" : "คัดลอกรหัส"}
                    </button>
                  </div>
                  <div className="mc-meta">
                    ผู้ใช้ได้ส่วนลด <b>{c.type === "percent" ? `${c.value}%` : baht(c.value)}</b> ของค่าธรรมเนียม
                    {c.type === "percent" && c.maxDiscount ? ` (สูงสุด ${baht(c.maxDiscount)})` : ""}
                    {c.minPrice ? ` · ราคาสินค้าขั้นต่ำ ${baht(c.minPrice)}` : ""}<br />
                    คุณได้ส่วนแบ่ง <b>{c.ownerSharePercent ?? 0}%</b> ของค่าธรรมเนียมที่เก็บจริง ·
                    ถูกใช้ <b>{c.usedCount || 0}{c.maxUses ? `/${c.maxUses}` : ""}</b> ครั้ง ·
                    ได้รับแล้ว <b>{baht(s.amount)}</b> จาก {s.deals} ดีล
                    {exp ? ` · หมดอายุ ${fmtDate(exp)}` : ""}
                  </div>
                </div>
              );
            })}

            <div className="mc-sec">ประวัติส่วนแบ่ง ({stats.history.length})</div>
            {stats.history.length === 0 ? (
              <div className="mc-empty">ยังไม่มีส่วนแบ่ง — จะเข้ามาเมื่อดีลที่ใช้คูปองของคุณสำเร็จ</div>
            ) : (
              stats.history.map((t) => (
                <div key={t.id} className="mc-item">
                  <div className="mc-item-body">
                    <div className="mc-item-name">คูปอง {t.couponCode || ""}</div>
                    <div className="mc-item-meta">{fmtDate(toMs(t.createdAt))} · ค่าธรรมเนียมดีล {baht(t.fee)}</div>
                  </div>
                  <div className="mc-item-amt">+{baht(t.couponShare)}</div>
                </div>
              ))
            )}
          </>
        )}
      </div>
    </div>
  );
}
